import { useContext, useState } from 'react';
import _ from 'lodash';
import { kmlGen } from '@tmcw/togeojson/dist/togeojson.es';
import { clone, convex, featureCollection } from '@turf/turf';
import { useParams } from 'react-router';
import { MapContext } from '../../../contexts/MapContext';
import { newImportedSection } from '../../../utils/field';
import ImportKmlButton from './ImportKmlButton';
import { UNKNOWN_CROP } from '../../../constants/fields';
import { addPointsAndRows } from '../../../utils/points-and-rows/addPointsAndRows';
import { isDuplicateFieldname, uniqueName } from '../../../utils/utils';
import { asQuantityWithUnit } from '../../../utils/formatters';

/**
 * Input: "Farmname: Aaron's Cat Farm, Farmsection: A, Rowname: 21, Species: Blackcurrant"
 * Output: {
 *   farmname: 'Aaron's Cat Farm',
 *   farmsection: 'A',
 *   rowname: '21',
 *   species: 'Blackcurrant'
 * }
 */
const getPropertiesMap = (propertiesString) => {
  if (!propertiesString) return {};
  return propertiesString
    .split(', ')
    .reduce(
      (map, entry) =>
        (([k, v]) => Object.assign(map, { [k.toLowerCase()]: v }))(
          entry.split(': '),
        ),
      {},
    );
};

/**
 * Generates a fieldSection for a group of imported rows.
 * rowsInfo is an array of objects like:
 * { row, crop, rowOrder }
 */
const toFieldSection = (sectionName, rowsInfo, farmId, dataMaps) => {
  const allRows = [];
  const rowOverrides = [];
  const orderedRowInfo = _.orderBy(rowsInfo, 'rowOrder');
  const primaryCropName = _.maxBy(
    Object.entries(_.countBy(rowsInfo, 'crop.name')),
    ([, count]) => count,
  )[0];
  const primaryCrop = _.find(rowsInfo, { crop: { name: primaryCropName } })
    .crop;
  orderedRowInfo.forEach(({ row, crop }, i) => {
    allRows.push(row);
    if (crop.name !== primaryCropName) {
      rowOverrides[i] = {
        cropName: crop.name,
        inRowSpacing: crop.inRowSpacing,
      };
    }
  });

  const rows = featureCollection(allRows);
  const fieldFeature = convex(rows, { concavity: 3 }) || allRows[0];
  const section = newImportedSection({
    farmId,
    name: sectionName,
    crop: primaryCrop,
    rows,
    rowOverrides,
    fieldFeature,
  });
  return addPointsAndRows(section, dataMaps);
};

/**
 * Returns a row feature that always starts from the westernmost end.
 */
const normalizeRow = (rowFeature) => {
  const { coordinates } = rowFeature.geometry;
  if (coordinates[0][0] > coordinates[coordinates.length - 1][0]) {
    const normalizedRow = clone(rowFeature);
    normalizedRow.geometry.coordinates = coordinates.reverse();
    return normalizedRow;
  }
  return rowFeature;
};

const ImportFieldsButton = (buttonProps) => {
  const { farmId } = useParams();
  const {
    crops: { availableCrops, cropNameToId },
    fields: { fieldSections, addFieldSections },
    patterns: { inRowPatternsMap },
  } = useContext(MapContext);
  const [messages, setMessages] = useState({});

  const onParse = (parsedData, filename = '') => {
    const fieldSectionNameList = fieldSections.map((section) => section.name);
    const rowsBySection = {};
    const errors = [];
    const warnings = [];
    const featuresIt = kmlGen(parsedData);

    // Add valid rows to importedSections (keyed by section).
    let featureCount = 0;
    let current = featuresIt.next();

    const uniqueFilename = filename
      ? fieldSectionNameList.includes(filename)
        ? uniqueName(
            filename,
            fieldSectionNameList
              .filter((name) => isDuplicateFieldname(filename, name))
              .map((name) => ({ name })),
          )
        : filename
      : '';
    while (!current.done) {
      const { value } = current;
      const row = normalizeRow(value);
      const { name: featureName } = value.properties;
      try {
        const properties = getPropertiesMap(featureName);
        const crop =
          (properties.species &&
            availableCrops.find(({ name }) => name === properties.species)) ||
          UNKNOWN_CROP;
        if (crop === UNKNOWN_CROP && properties.species != null) {
          warnings.push(
            `Couldn't match crop: ${properties.species} in row ${featureName}`,
          );
        }
        row.properties.cropName = crop.name;
        const sectionName =
          properties.farmsection || uniqueFilename || 'Imported section';
        rowsBySection[sectionName] = rowsBySection[sectionName] || [];
        rowsBySection[sectionName].push({
          row,
          crop,
          // Rows that are not parseable ints will be sorted to the end.
          rowOrder: parseInt(properties.rowname, 10),
        });
      } catch (err) {
        errors.push(
          `Error parsing row ${featureName || featureCount}: ${err.message}`,
        );
      }
      featureCount += 1;
      current = featuresIt.next();
    }

    const sectionsArray = [];
    Object.entries(rowsBySection).forEach(([sectionName, rowInfos]) => {
      try {
        sectionsArray.push(
          toFieldSection(sectionName, rowInfos, farmId, {
            cropMap: availableCrops,
            cropNameToId,
            inRowPatternsMap,
          }),
        );
      } catch (err) {
        errors.push(`Error saving parsed sections: ${err.message}`);
      }
    });
    addFieldSections(sectionsArray);
    setMessages({
      info: `Imported ${asQuantityWithUnit(
        'valid sections',
        'valid section',
      )(sectionsArray.length)}. ${asQuantityWithUnit(
        'sections',
        'section',
      )(errors.length)} skipped due to errors.`,
      // Sections with warnings are still imported
      warning: warnings.length ? warnings.join('\n') : null,
      // Sections with errors are skipped
      error: errors.length ? errors.join('\n') : null,
    });
  };
  return (
    <ImportKmlButton
      onParse={onParse}
      messages={messages}
      setMessages={setMessages}
      outline
      size="sm"
      disabled={!availableCrops}
      {...buttonProps}
    >
      Upload
    </ImportKmlButton>
  );
};

export default ImportFieldsButton;
